import React from 'react';

import type { MerchantReportRiskLevel } from '@ballerine/common';

import { ctw } from '@/common';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms';
import { Hexagon } from '../svg/Hexagon';

const RISK_LEVELS = [
  {
    level: 'low',
    label: 'Low Risk',
    description:
      'Legitimate posture with no material red flags and a transparent, consistent footprint.',
    gradient: 'linear-gradient(180deg, #00BD59 0%, #009D4A 100%)',
  },
  {
    level: 'medium',
    label: 'Medium Risk',
    description: 'Moderate or minor red flags are present, but not systemic or clearly confirmed.',
    gradient: 'linear-gradient(180deg, #FFB35A 0%, #F1870A 100%)',
  },
  {
    level: 'high',
    label: 'High Risk',
    description:
      'Multiple red flags across areas indicate a high likelihood of harmful or non-compliant activity.',
    gradient: 'linear-gradient(180deg, #FF7300 0%, #E44800 100%)',
  },
  {
    level: 'critical',
    label: 'Critical Risk',
    description: 'Clear, substantiated evidence of severe non-compliance or illegality.',
    gradient: 'linear-gradient(180deg, #FF3300 0%, #A60000 100%)',
  },
] as const;

const RiskLevelTooltipContent: React.FC<{
  currentRiskLevel?: MerchantReportRiskLevel | null;
}> = ({ currentRiskLevel }) => (
  <div className="space-y-3">
    {RISK_LEVELS.map(({ level, label, description, gradient }) => {
      const isCurrentLevel = currentRiskLevel === level;

      return (
        <div key={level} className="flex items-center gap-3">
          <span
            className="w-28 shrink-0 rounded-md px-3 py-1.5 text-center text-sm font-semibold text-white"
            style={{ background: gradient }}
          >
            {label}
          </span>
          <span
            className={ctw(
              'line-clamp-2 flex-1 text-left text-sm',
              isCurrentLevel ? 'text-black' : 'text-gray-400',
            )}
          >
            {description}
          </span>
        </div>
      );
    })}
  </div>
);

export const RiskLevelHexagon: React.FC<{
  className?: string;
  riskLevel?: MerchantReportRiskLevel | null;
}> = ({ className, riskLevel }) => {
  return (
    <div className={ctw('relative inline-block', className)} data-testid="risk-level-value">
      <Hexagon riskLevel={riskLevel} />
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="absolute -right-0 bottom-[1.6rem] flex h-5 w-5 cursor-help items-center justify-center rounded-full border-2 border-white bg-white/20 text-sm font-bold text-white backdrop-blur-sm"
              aria-label="Understanding risk levels"
            >
              ?
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            className="max-w-lg border border-slate-200 bg-white p-4 shadow-lg"
          >
            <p className="mb-3 text-left text-sm font-semibold text-slate-900">
              Understanding Risk Levels
            </p>
            <RiskLevelTooltipContent currentRiskLevel={riskLevel} />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
